import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Send, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    enquiry_type: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, enquiry_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        enquiry_type: "",
        message: ""
      });
    } catch (err) {
      setError("Failed to submit enquiry. Please try again or contact us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen flex flex-col" data-testid="contact-page">
      <Navigation />
      
      <main className="flex-1 relative z-10 px-8 lg:px-16 pt-40 pb-24">
        <div className="max-w-7xl mx-auto">
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h1 
              className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl font-medium text-white tracking-tight mb-6"
              data-testid="contact-heading"
            >
              Get in Touch
            </h1>
            <p className="text-lg md:text-xl font-light leading-relaxed text-slate-300">
              Have a question or ready to discuss your funding needs? 
              We'd love to hear from you.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:col-span-1"
            >
              <div className="glass-card p-8 rounded-sm h-full">
                <h2 className="font-['Playfair_Display'] text-2xl text-white font-medium mb-8">
                  Contact Information
                </h2>
                
                <div className="space-y-6">
                  <a
                    href="tel:1300021888"
                    className="flex items-start gap-4 group"
                    data-testid="contact-phone"
                  >
                    <div className="w-12 h-12 rounded-sm bg-[#bd9a59]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#bd9a59]/20 transition-colors">
                      <Phone className="w-5 h-5 text-[#bd9a59]" />
                    </div>
                    <div>
                      <p className="text-slate-500 text-sm mb-1">Phone</p>
                      <p className="text-white font-medium group-hover:text-[#bd9a59] transition-colors">
                        1300 021 888
                      </p>
                    </div>
                  </a>

                  <a
                    href="mailto:loans@sgcap.com.au"
                    className="flex items-start gap-4 group"
                    data-testid="contact-email"
                  >
                    <div className="w-12 h-12 rounded-sm bg-[#bd9a59]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#bd9a59]/20 transition-colors">
                      <Mail className="w-5 h-5 text-[#bd9a59]" />
                    </div>
                    <div>
                      <p className="text-slate-500 text-sm mb-1">Email</p>
                      <p className="text-white font-medium group-hover:text-[#bd9a59] transition-colors">
                        loans@sgcap.com.au
                      </p>
                    </div>
                  </a>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-sm bg-[#bd9a59]/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-[#bd9a59]" />
                    </div>
                    <div>
                      <p className="text-slate-500 text-sm mb-1">Office</p>
                      <p className="text-white font-medium">
                        Level 1, 60 Martin Place<br />
                        Sydney, NSW 2000
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-10 pt-8 border-t border-white/10">
                  <p className="text-slate-400 text-sm mb-4">Business Hours</p>
                  <p className="text-white text-sm">
                    Monday - Friday: 9:00 AM - 6:00 PM<br />
                    Saturday - Sunday: Closed
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="lg:col-span-2"
            >
              <div className="glass-card p-8 rounded-sm">
                {isSubmitted ? (
                  <div className="text-center py-12" data-testid="contact-success">
                    <CheckCircle className="w-16 h-16 text-[#bd9a59] mx-auto mb-6" />
                    <h3 className="font-['Playfair_Display'] text-2xl text-white font-medium mb-4">
                      Thank You!
                    </h3>
                    <p className="text-slate-400 mb-8">
                      Your enquiry has been received. A member of our team will be in touch shortly.
                    </p>
                    <Button
                      onClick={() => setIsSubmitted(false)}
                      className="bg-[#bd9a59] text-slate-950 hover:bg-[#d4b270] rounded-sm px-6 py-3"
                    >
                      Submit Another Enquiry
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-testid="contact-form">
                    <h2 className="font-['Playfair_Display'] text-2xl text-white font-medium mb-8">
                      Send an Enquiry
                    </h2>

                    {error && (
                      <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-4 mb-6">
                        <p className="text-red-400 text-sm">{error}</p>
                      </div>
                    )}

                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <Label htmlFor="name" className="text-slate-300 mb-2 block">
                          Full Name *
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="bg-slate-950/50 border-white/10 focus:border-[#bd9a59] text-white h-12"
                          placeholder="John Smith"
                          data-testid="contact-name-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-slate-300 mb-2 block">
                          Email Address *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="bg-slate-950/50 border-white/10 focus:border-[#bd9a59] text-white h-12"
                          placeholder="john@company.com"
                          data-testid="contact-email-input"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <Label htmlFor="phone" className="text-slate-300 mb-2 block">
                          Phone Number
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          className="bg-slate-950/50 border-white/10 focus:border-[#bd9a59] text-white h-12"
                          placeholder="0400 000 000"
                          data-testid="contact-phone-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="company" className="text-slate-300 mb-2 block">
                          Company Name
                        </Label>
                        <Input
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          className="bg-slate-950/50 border-white/10 focus:border-[#bd9a59] text-white h-12"
                          placeholder="Your Company Pty Ltd"
                          data-testid="contact-company-input"
                        />
                      </div>
                    </div>

                    <div className="mb-6">
                      <Label htmlFor="enquiry_type" className="text-slate-300 mb-2 block">
                        Enquiry Type *
                      </Label>
                      <Select onValueChange={handleSelectChange} value={formData.enquiry_type} required>
                        <SelectTrigger 
                          className="bg-slate-950/50 border-white/10 focus:border-[#bd9a59] text-white h-12"
                          data-testid="contact-enquiry-type-select"
                        >
                          <SelectValue placeholder="Select enquiry type" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-white/10">
                          <SelectItem value="development_finance">Development Finance</SelectItem>
                          <SelectItem value="construction_loan">Construction Loan</SelectItem>
                          <SelectItem value="land_acquisition">Land Acquisition</SelectItem>
                          <SelectItem value="mezzanine_funding">Mezzanine Funding</SelectItem>
                          <SelectItem value="general_enquiry">General Enquiry</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="mb-8">
                      <Label htmlFor="message" className="text-slate-300 mb-2 block">
                        Message *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="bg-slate-950/50 border-white/10 focus:border-[#bd9a59] text-white resize-none"
                        placeholder="Tell us about your project or enquiry..."
                        data-testid="contact-message-input"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full md:w-auto bg-[#bd9a59] text-slate-950 hover:bg-[#d4b270] rounded-sm px-8 py-6 text-sm font-semibold tracking-wide uppercase transition-all duration-300 shadow-[0_0_20px_rgba(189,154,89,0.3)] hover:shadow-[0_0_30px_rgba(189,154,89,0.5)] disabled:opacity-50"
                      data-testid="contact-submit-btn"
                    >
                      {isSubmitting ? (
                        "Sending..."
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Enquiry
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
